import { useState, useEffect, useCallback } from 'react';

export interface RotatingWordItem {
  word: string;
  article?: string;
}

interface UseTextRotatorOptions {
  words: RotatingWordItem[] | string[];
  interval?: number;
}

// Helper to normalize words to RotatingWordItem format
const normalizeWord = (word: string | RotatingWordItem): RotatingWordItem => {
  if (typeof word === 'string') {
    return { word, article: '' };
  }
  return word;
};

export const useTextRotator = ({ words, interval = 3000 }: UseTextRotatorOptions) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const normalizedWords = words.map(normalizeWord);
  const [displayWord, setDisplayWord] = useState<RotatingWordItem>(normalizedWords[0]);

  // Reset when words array changes (e.g., language switch)
  useEffect(() => {
    const normalized = words.map(normalizeWord);
    setCurrentIndex(0);
    setDisplayWord(normalized[0]);
    setIsAnimating(false);
  }, [JSON.stringify(words)]);

  const rotateWord = useCallback(() => {
    setIsAnimating(true);
    
    // After exit animation completes, change word and trigger enter animation
    setTimeout(() => {
      setCurrentIndex((prev) => {
        const nextIndex = (prev + 1) % normalizedWords.length;
        setDisplayWord(normalizedWords[nextIndex]);
        return nextIndex;
      });
      setIsAnimating(false);
    }, 400); // Duration of exit animation
  }, [normalizedWords]);

  useEffect(() => {
    const timer = setInterval(rotateWord, interval);
    return () => clearInterval(timer);
  }, [rotateWord, interval]);

  return { 
    currentWord: displayWord.word,
    currentArticle: displayWord.article || '',
    isAnimating 
  };
};
